import { Link, ModuleBase, UserContext } from '@/components';
import { LinkModel, ModuleBaseModel, ModuleBasePropsModel, SubscriptionTag } from '@/types';
import { useContext, useEffect, useMemo, useState } from 'react';
import XLight from '@/assets/xLight.svg';
import classNames from 'classnames';
import ArrowRightLight from '@/assets/arrowRightLight.svg';
import { saveUsersSubscribedTags } from '@/api/personalisation-config';
import { useRouter } from 'next/router';

type SubsCfgWizardModel = ModuleBaseModel<
  {
    finishedLink: LinkModel;
    introduction: string;
    skipLink: LinkModel;
    status: {
      wantsEmail: boolean;
      weeklyAlert: boolean;
    };
    subscriptions: SubscriptionTag[];
    title: string;
  } & ModuleBasePropsModel
>;

const SubsCfgWizard = (data: SubsCfgWizardModel) => {
  const {
    props: { finishedLink, introduction, skipLink, subscriptions, title, status },
  } = data;
  const { userId } = useContext(UserContext);
  const router = useRouter();

  const [displaying, setDisplaying] = useState<string>('Intro');
  const [selectedSubscriptions, setSelectedSubscriptions] = useState<SubscriptionTag[]>([]);
  const [mailAlertFrequency, setMailAlertFrequency] = useState<string>('None');

  useEffect(() => {
    const initiallySelected = subscriptions.filter((sub) => sub.subscribed);
    setSelectedSubscriptions(initiallySelected);

    if (status.weeklyAlert) {
      setMailAlertFrequency('Weekly');
    } else if (status.wantsEmail) {
      setMailAlertFrequency('Daily');
    } else {
      setMailAlertFrequency('None');
    }
  }, [subscriptions, status]);

  const groupedSubscriptions: { name: string; subs: SubscriptionTag[] }[] = useMemo(() => {
    const result: { name: string; subs: SubscriptionTag[] }[] = [
      { name: 'Directories', subs: [] },
      { name: 'Topics', subs: [] },
      { name: 'Generations', subs: [] },
      { name: 'Regions', subs: [] },
    ];
    subscriptions.forEach((subscription) => {
      let groupName = subscription.group || 'Ungrouped';
      if (groupName === 'Region') {
        groupName = 'Regions';
      }

      const group = result.find((g) => g.name === groupName);
      if (group) {
        group.subs.push(subscription);
      }
    });

    return result.filter((g) => g.subs.length > 0);
  }, [subscriptions]);

  const toggleSubscription = (sub: SubscriptionTag) => {
    const isSelected = selectedSubscriptions.find((s) => s.tagId === sub.tagId);
    if (isSelected) {
      setSelectedSubscriptions(selectedSubscriptions.filter((s) => s.tagId !== sub.tagId));
    } else {
      setSelectedSubscriptions([...selectedSubscriptions, sub]);
    }
  };

  const onNextClick = () => {
    if (displaying === 'Intro') {
      setDisplaying(groupedSubscriptions[0].name);
    } else {
      const pos = groupedSubscriptions.findIndex((f) => f.name === displaying);
      if (pos < 0 || pos == groupedSubscriptions.length - 1) {
        setDisplaying('Outro');
      } else {
        setDisplaying(groupedSubscriptions[1 + pos].name);
      }
    }
  };

  const onBackClick = () => {
    if (displaying === 'Outro') {
      const lastGroup = groupedSubscriptions[groupedSubscriptions.length - 1];
      setDisplaying(lastGroup.name);
    } else {
      const pos = groupedSubscriptions.findIndex((f) => f.name === displaying);
      if (pos > 0) {
        setDisplaying(groupedSubscriptions[pos - 1].name);
      } else {
        setDisplaying('Intro');
      }
    }
  };

  const onSaveClick = () => {
    if (userId) {
      saveUsersSubscribedTags({
        userId,
        selectedTags: selectedSubscriptions,
        status: {
          hasDoneSetup: true,
          isSubscribed: true,
          wantsEmail: mailAlertFrequency !== 'None',
          weeklyAlert: mailAlertFrequency === 'Weekly',
          dailyAlert: mailAlertFrequency === 'Daily',
        },
      })
        .then(() => {
          router.push(finishedLink.href);
        })
        .catch(() => {
          console.error('There was an error saving your subscriptions. Please try again.');
        });
    }
  };

  const activeSubscriptionGrouped = groupedSubscriptions.find((g) => g.name === displaying);

  return (
    <ModuleBase data={data} noPadding>
      <div className="container flex min-h-[calc(100dvh-var(--header-height))] items-center justify-center py-10">
        {displaying === 'Intro' ? (
          <div>
            <h3 className="text-center text-[18.72px] font-bold" dangerouslySetInnerHTML={{ __html: title }} />
            <div dangerouslySetInnerHTML={{ __html: introduction }} className="mt-2 mb-4"></div>
            <div className="flex justify-center gap-1">
              <button className="btn secondary !border-black !px-7 !text-sm !font-medium" onClick={onNextClick}>
                Let&apos;s start…
              </button>
              <Link
                link={skipLink}
                className="btn primary !px-7 !text-sm !font-medium hover:!bg-black/80 hover:!text-white"
              >
                I&apos;ve done this before
              </Link>
            </div>
          </div>
        ) : displaying === 'Outro' ? (
          <div>
            <h3 className="text-center text-2xl font-bold">Your selection</h3>
            <div className="mt-4 flex flex-col items-center">
              <div className="flex w-1/2 flex-wrap justify-center px-3">
                {selectedSubscriptions.map((sub) => (
                  <div
                    key={sub.tagId}
                    className="m-1 flex items-center rounded-full bg-black px-3 py-1 text-xs font-medium text-white"
                  >
                    {sub.name}
                  </div>
                ))}
              </div>
              <div className="pt-4">
                <h2 className="mb-5 text-center text-2xl font-bold">Email alert frequency</h2>
                <div className="flex gap-1">
                  <button
                    className={classNames(
                      'btn secondary flex !border-black !px-7 !text-sm !font-medium',
                      mailAlertFrequency === 'None' && '!bg-black !text-white hover:!bg-gray-800',
                    )}
                    onClick={() => setMailAlertFrequency('None')}
                  >
                    None
                  </button>
                  <button
                    className={classNames(
                      'btn secondary flex !border-black !px-7 !text-sm !font-medium',
                      mailAlertFrequency === 'Weekly' && '!bg-black !text-white hover:!bg-gray-800',
                    )}
                    onClick={() => setMailAlertFrequency('Weekly')}
                  >
                    Weekly
                  </button>
                  <button
                    className={classNames(
                      'btn secondary flex !border-black !px-7 !text-sm !font-medium',
                      mailAlertFrequency === 'Daily' && '!bg-black !text-white hover:!bg-gray-800',
                    )}
                    onClick={() => setMailAlertFrequency('Daily')}
                  >
                    Daily
                  </button>
                </div>
              </div>
              <div className="mt-4 flex justify-center gap-1">
                <button className="btn secondary !border-black !px-7 !text-sm !font-medium" onClick={onBackClick}>
                  <ArrowRightLight className="h-4.5 w-4 rotate-180 !fill-black" /> Back
                </button>
                <button
                  className="btn primary !px-7 !text-sm !font-medium hover:!bg-black/80 hover:!text-white"
                  onClick={onSaveClick}
                >
                  Save...
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <h3 className="text-center text-[18.72px] font-bold">{activeSubscriptionGrouped?.name}</h3>
            <div className="mt-4 flex flex-col items-center">
              <div className="flex w-1/2 flex-wrap justify-center px-3">
                {activeSubscriptionGrouped?.subs.map((sub) => (
                  <button
                    key={sub.tagId}
                    className={classNames(
                      'm-2 flex cursor-pointer items-center gap-1.5 rounded-full border py-2.5 pr-3 pl-5 text-sm font-medium transition-all duration-200',
                      selectedSubscriptions.find((s) => s.tagId === sub.tagId)
                        ? 'bg-black text-white hover:bg-gray-800'
                        : 'bg-white text-gray-700 hover:bg-gray-100',
                    )}
                    onClick={() => toggleSubscription(sub)}
                  >
                    {sub.name}
                    <XLight
                      className={classNames(
                        'h-4 w-4 transition-all duration-200',
                        selectedSubscriptions.find((s) => s.tagId === sub.tagId)
                          ? 'fill-white opacity-100'
                          : 'opacity-0',
                      )}
                    />
                  </button>
                ))}
              </div>
              <div className="mt-4 flex justify-center gap-1">
                <button className="btn secondary !border-black !px-7 !text-sm !font-medium" onClick={onBackClick}>
                  <ArrowRightLight className="h-4.5 w-4 rotate-180 !fill-black" /> Back
                </button>
                <button
                  className="btn primary !px-7 !text-sm !font-medium hover:!bg-black/80 hover:!text-white"
                  onClick={onNextClick}
                >
                  Continue…
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </ModuleBase>
  );
};

export default SubsCfgWizard;
